import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger
} from '../components/ui/collapsible';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  Plus, Search, Megaphone, Calendar, MoreHorizontal, Edit, Trash2, Eye,
  Filter, Loader2, Mail, Facebook, DollarSign, Globe, ChevronDown, ChevronRight,
  Target, Users, FileText, Image, BarChart3, Clock, CheckCircle2, AlertCircle,
  Inbox, PlayCircle, Archive, RefreshCw, TrendingUp
} from 'lucide-react';
import api from '../api';

const CAMPAIGN_TYPES = [
  { value: 'email', label: 'Email Campaign', icon: Mail },
  { value: 'social_media', label: 'Social Media', icon: Facebook },
  { value: 'paid_ads', label: 'Paid Ads', icon: DollarSign },
  { value: 'event', label: 'Event', icon: Calendar },
  { value: 'content', label: 'Content Marketing', icon: Globe },
  { value: 'multi_channel', label: 'Multi-Channel', icon: Megaphone }
];

// Status groupings for tabs
const STATUS_GROUPS = {
  planning: ['draft', 'pending_review', 'legal_review', 'revision_requested'],
  approved: ['approved', 'manager_approval'],
  in_progress: ['scheduled', 'live', 'paused'],
  completed: ['completed'],
  archived: ['rejected', 'cancelled', 'archived']
};

const TAB_CONFIG = [
  { id: 'all', label: 'All Campaigns', icon: Megaphone },
  { id: 'planning', label: 'Planning', icon: FileText },
  { id: 'approved', label: 'Approved', icon: CheckCircle2 },
  { id: 'in_progress', label: 'In Progress', icon: PlayCircle },
  { id: 'completed', label: 'Completed & Reporting', icon: BarChart3 },
  { id: 'archived', label: 'Archived', icon: Archive }
];

export const MarketingCampaignsPage = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    targeting: false,
    budget: false,
    content: false,
    assets: false
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setRefreshing(true);
      const res = await api.get('/marketing/campaigns');
      setCampaigns(res.data || []);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await api.delete(`/marketing/campaigns/${campaignId}`);
      toast.success('Campaign deleted');
      if (selectedCampaign?.id === campaignId) {
        setSelectedCampaign(null);
      }
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Draft', className: 'bg-slate-100 text-slate-700' },
      pending_review: { label: 'Pending Review', className: 'bg-blue-100 text-blue-700' },
      revision_requested: { label: 'Revision Requested', className: 'bg-orange-100 text-orange-700' },
      legal_review: { label: 'Legal Review', className: 'bg-purple-100 text-purple-700' },
      manager_approval: { label: 'Awaiting Approval', className: 'bg-amber-100 text-amber-700' },
      approved: { label: 'Approved', className: 'bg-green-100 text-green-700' },
      scheduled: { label: 'Scheduled', className: 'bg-cyan-100 text-cyan-700' },
      live: { label: 'Live', className: 'bg-emerald-100 text-emerald-700 animate-pulse' },
      paused: { label: 'Paused', className: 'bg-orange-100 text-orange-700' },
      completed: { label: 'Completed', className: 'bg-slate-100 text-slate-600' },
      rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700' },
      cancelled: { label: 'Cancelled', className: 'bg-slate-200 text-slate-500' },
      archived: { label: 'Archived', className: 'bg-slate-200 text-slate-500' }
    };
    const config = statusConfig[status] || { label: status, className: 'bg-slate-100 text-slate-700' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getCampaignTypeIcon = (type) => {
    const typeConfig = CAMPAIGN_TYPES.find(t => t.value === type);
    if (typeConfig) {
      const Icon = typeConfig.icon;
      return <Icon className="h-4 w-4" />;
    }
    return <Megaphone className="h-4 w-4" />;
  };

  // Filter campaigns by tab and search
  const filteredCampaigns = useMemo(() => {
    let filtered = campaigns;
    
    // Filter by tab
    if (activeTab !== 'all') {
      const statuses = STATUS_GROUPS[activeTab] || [];
      filtered = filtered.filter(c => statuses.includes(c.status));
    }
    
    // Filter by search
    if (searchTerm) {
      filtered = filtered.filter(campaign =>
        campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        campaign.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  }, [campaigns, activeTab, searchTerm]);

  // Get counts for each tab
  const tabCounts = useMemo(() => {
    const counts = { all: campaigns.length };
    Object.entries(STATUS_GROUPS).forEach(([group, statuses]) => {
      counts[group] = campaigns.filter(c => statuses.includes(c.status)).length;
    });
    return counts;
  }, [campaigns]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="marketing-campaigns-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Megaphone className="h-6 w-6" />
            Marketing Campaigns
          </h1>
          <p className="text-muted-foreground">
            Manage campaigns through planning, execution, and reporting
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={fetchCampaigns} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          {hasRole(['admin', 'marketing_manager']) && (
            <Link to="/marketing/campaigns/new">
              <Button data-testid="create-campaign-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Campaign
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Status Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full justify-start h-auto p-1 flex-wrap">
          {TAB_CONFIG.map(tab => {
            const Icon = tab.icon;
            return (
              <TabsTrigger 
                key={tab.id} 
                value={tab.id}
                className="flex items-center gap-2 px-4"
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                <Badge variant="secondary" className="ml-1 text-xs">
                  {tabCounts[tab.id] || 0}
                </Badge>
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search campaigns..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
          data-testid="search-campaigns"
        />
      </div>

      {/* Main Content - Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Campaign List */}
        <div className="lg:col-span-1">
          <Card className="h-[calc(100vh-320px)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {TAB_CONFIG.find(t => t.id === activeTab)?.label || 'Campaigns'}
              </CardTitle>
              <CardDescription>
                {filteredCampaigns.length} campaign{filteredCampaigns.length !== 1 ? 's' : ''}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-430px)]">
                {filteredCampaigns.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Inbox className="h-12 w-12 mb-3 opacity-30" />
                    <p>No campaigns found</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredCampaigns.map((campaign) => (
                      <div
                        key={campaign.id}
                        className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                          selectedCampaign?.id === campaign.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                        }`}
                        onClick={() => setSelectedCampaign(campaign)}
                        data-testid={`campaign-item-${campaign.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {getCampaignTypeIcon(campaign.campaign_type)}
                            <span className="font-medium truncate max-w-[180px]">{campaign.name}</span>
                          </div>
                          {getStatusBadge(campaign.status)}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
                          {campaign.description || 'No description'}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          {campaign.start_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {format(parseISO(campaign.start_date), 'MMM d')}
                            </span>
                          )}
                          {campaign.budget && (
                            <span className="flex items-center gap-1">
                              <DollarSign className="h-3 w-3" />
                              {formatCurrency(campaign.budget).replace('.00', '')}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Campaign Detail - Split View */}
        <div className="lg:col-span-2">
          {selectedCampaign ? (
            <Card className="h-[calc(100vh-320px)]">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getCampaignTypeIcon(selectedCampaign.campaign_type)}
                      <CardTitle>{selectedCampaign.name}</CardTitle>
                      {getStatusBadge(selectedCampaign.status)}
                    </div>
                    <CardDescription>{selectedCampaign.description || 'No description provided'}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to={`/marketing/campaigns/${selectedCampaign.id}`}>
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4 mr-1" />
                        Full View
                      </Button>
                    </Link>
                    <Link to={`/marketing/campaigns/${selectedCampaign.id}/edit`}>
                      <Button variant="outline" size="sm">
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </Link>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleDeleteCampaign(selectedCampaign.id)}>
                          <Trash2 className="h-4 w-4 mr-2 text-red-500" />
                          Delete Campaign
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardHeader>
              <Separator />
              <CardContent className="p-0">
                <ScrollArea className="h-[calc(100vh-480px)]">
                  <div className="p-4 space-y-2">
                    {/* Basic Info Section */}
                    <Collapsible open={expandedSections.basic} onOpenChange={() => toggleSection('basic')}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          <span className="font-medium">Basic Information</span>
                        </div>
                        {expandedSections.basic ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </CollapsibleTrigger>
                      <CollapsibleContent className="p-4 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Campaign Type</p>
                            <p className="font-medium capitalize">{selectedCampaign.campaign_type?.replace('_', ' ')}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Channels</p>
                            <div className="flex flex-wrap gap-1">
                              {(selectedCampaign.channels || []).map(ch => (
                                <Badge key={ch} variant="outline" className="text-xs">{ch}</Badge>
                              ))}
                              {(!selectedCampaign.channels || selectedCampaign.channels.length === 0) && (
                                <span className="text-muted-foreground text-sm">None selected</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Start Date</p>
                            <p className="font-medium">
                              {selectedCampaign.start_date ? format(parseISO(selectedCampaign.start_date), 'MMM d, yyyy') : 'Not set'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">End Date</p>
                            <p className="font-medium">
                              {selectedCampaign.end_date ? format(parseISO(selectedCampaign.end_date), 'MMM d, yyyy') : 'Not set'}
                            </p>
                          </div>
                        </div>
                        {selectedCampaign.tags && selectedCampaign.tags.length > 0 && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-2">Tags</p>
                            <div className="flex flex-wrap gap-1">
                              {selectedCampaign.tags.map(tag => (
                                <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Targeting Section */}
                    <Collapsible open={expandedSections.targeting} onOpenChange={() => toggleSection('targeting')}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
                        <div className="flex items-center gap-2">
                          <Target className="h-4 w-4" />
                          <span className="font-medium">Targeting & Audience</span>
                        </div>
                        {expandedSections.targeting ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </CollapsibleTrigger>
                      <CollapsibleContent className="p-4 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Target Segments</p>
                            <div className="flex flex-wrap gap-1">
                              {(selectedCampaign.target_segments || []).map(seg => (
                                <Badge key={seg} variant="outline" className="text-xs">{seg}</Badge>
                              ))}
                              {(!selectedCampaign.target_segments || selectedCampaign.target_segments.length === 0) && (
                                <span className="text-muted-foreground text-sm">All segments</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Product Lines</p>
                            <div className="flex flex-wrap gap-1">
                              {(selectedCampaign.target_product_lines || []).map(pl => (
                                <Badge key={pl} variant="outline" className="text-xs">{pl}</Badge>
                              ))}
                              {(!selectedCampaign.target_product_lines || selectedCampaign.target_product_lines.length === 0) && (
                                <span className="text-muted-foreground text-sm">All products</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Audience Size</p>
                            <p className="font-medium">
                              {selectedCampaign.target_audience_size ? parseInt(selectedCampaign.target_audience_size).toLocaleString() : 'Not estimated'}
                            </p>
                          </div>
                        </div>
                        {selectedCampaign.kpis && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-2">Target KPIs</p>
                            <div className="grid grid-cols-4 gap-2">
                              {selectedCampaign.kpis.target_impressions && (
                                <div className="text-center p-2 bg-blue-50 rounded">
                                  <p className="text-lg font-bold text-blue-700">{parseInt(selectedCampaign.kpis.target_impressions).toLocaleString()}</p>
                                  <p className="text-xs text-muted-foreground">Impressions</p>
                                </div>
                              )}
                              {selectedCampaign.kpis.target_clicks && (
                                <div className="text-center p-2 bg-green-50 rounded">
                                  <p className="text-lg font-bold text-green-700">{parseInt(selectedCampaign.kpis.target_clicks).toLocaleString()}</p>
                                  <p className="text-xs text-muted-foreground">Clicks</p>
                                </div>
                              )}
                              {selectedCampaign.kpis.target_conversions && (
                                <div className="text-center p-2 bg-purple-50 rounded">
                                  <p className="text-lg font-bold text-purple-700">{parseInt(selectedCampaign.kpis.target_conversions).toLocaleString()}</p>
                                  <p className="text-xs text-muted-foreground">Conversions</p>
                                </div>
                              )}
                              {selectedCampaign.kpis.target_leads && (
                                <div className="text-center p-2 bg-amber-50 rounded">
                                  <p className="text-lg font-bold text-amber-700">{parseInt(selectedCampaign.kpis.target_leads).toLocaleString()}</p>
                                  <p className="text-xs text-muted-foreground">Leads</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Budget Section */}
                    <Collapsible open={expandedSections.budget} onOpenChange={() => toggleSection('budget')}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4" />
                          <span className="font-medium">Budget & ROI</span>
                        </div>
                        {expandedSections.budget ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </CollapsibleTrigger>
                      <CollapsibleContent className="p-4 space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div className="p-4 bg-green-50 rounded-lg text-center">
                            <p className="text-2xl font-bold text-green-700">{formatCurrency(selectedCampaign.budget)}</p>
                            <p className="text-xs text-muted-foreground">Total Budget</p>
                          </div>
                          <div className="p-4 bg-blue-50 rounded-lg text-center">
                            <p className="text-2xl font-bold text-blue-700">{formatCurrency(selectedCampaign.budget_spent || 0)}</p>
                            <p className="text-xs text-muted-foreground">Spent</p>
                          </div>
                          <div className="p-4 bg-amber-50 rounded-lg text-center">
                            <p className="text-2xl font-bold text-amber-700">
                              {selectedCampaign.expected_roi ? `${selectedCampaign.expected_roi}%` : 'N/A'}
                            </p>
                            <p className="text-xs text-muted-foreground">Expected ROI</p>
                          </div>
                        </div>
                        {selectedCampaign.budget && selectedCampaign.budget_spent > 0 && (
                          <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span>Budget Utilization</span>
                              <span>{Math.round((selectedCampaign.budget_spent / selectedCampaign.budget) * 100)}%</span>
                            </div>
                            <Progress value={(selectedCampaign.budget_spent / selectedCampaign.budget) * 100} />
                          </div>
                        )}
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Content Section */}
                    <Collapsible open={expandedSections.content} onOpenChange={() => toggleSection('content')}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          <span className="font-medium">Creative Content</span>
                          {selectedCampaign.content_items?.length > 0 && (
                            <Badge variant="secondary" className="text-xs">{selectedCampaign.content_items.length}</Badge>
                          )}
                        </div>
                        {expandedSections.content ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </CollapsibleTrigger>
                      <CollapsibleContent className="p-4 space-y-4">
                        {selectedCampaign.headline && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Headline</p>
                            <p className="font-medium text-lg">{selectedCampaign.headline}</p>
                          </div>
                        )}
                        {selectedCampaign.tagline && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Tagline</p>
                            <p className="italic">{selectedCampaign.tagline}</p>
                          </div>
                        )}
                        {selectedCampaign.call_to_action && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Call to Action</p>
                            <Badge className="bg-blue-600">{selectedCampaign.call_to_action}</Badge>
                          </div>
                        )}
                        {selectedCampaign.landing_page_url && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Landing Page</p>
                            <a href={selectedCampaign.landing_page_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                              {selectedCampaign.landing_page_url}
                            </a>
                          </div>
                        )}
                        {selectedCampaign.content_items && selectedCampaign.content_items.length > 0 && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-2">Content Items</p>
                            <div className="space-y-2">
                              {selectedCampaign.content_items.map((item, idx) => (
                                <div key={idx} className="p-2 border rounded bg-white">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge variant="outline" className="text-xs">{item.content_type}</Badge>
                                    <span className="font-medium text-sm">{item.title}</span>
                                  </div>
                                  {item.body && <p className="text-sm text-muted-foreground line-clamp-2">{item.body}</p>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Assets Section */}
                    <Collapsible open={expandedSections.assets} onOpenChange={() => toggleSection('assets')}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
                        <div className="flex items-center gap-2">
                          <Image className="h-4 w-4" />
                          <span className="font-medium">Assets</span>
                          {selectedCampaign.assets?.length > 0 && (
                            <Badge variant="secondary" className="text-xs">{selectedCampaign.assets.length}</Badge>
                          )}
                        </div>
                        {expandedSections.assets ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </CollapsibleTrigger>
                      <CollapsibleContent className="p-4">
                        {selectedCampaign.assets && selectedCampaign.assets.length > 0 ? (
                          <div className="grid grid-cols-3 gap-2">
                            {selectedCampaign.assets.map((asset, idx) => (
                              <div key={idx} className="p-2 border rounded text-center">
                                <Image className="h-8 w-8 mx-auto mb-1 text-muted-foreground" />
                                <p className="text-xs truncate">{asset.name}</p>
                                <Badge variant="outline" className="text-xs">{asset.type}</Badge>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground text-center py-4">No assets uploaded</p>
                        )}
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Metrics Section (for completed campaigns) */}
                    {selectedCampaign.status === 'completed' && (
                      <Collapsible defaultOpen>
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-green-50 rounded-lg hover:bg-green-100">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-green-700" />
                            <span className="font-medium text-green-700">Campaign Results</span>
                          </div>
                          <ChevronDown className="h-4 w-4" />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="p-4">
                          <div className="grid grid-cols-4 gap-4">
                            <div className="text-center p-3 bg-blue-50 rounded-lg">
                              <p className="text-2xl font-bold text-blue-700">{(selectedCampaign.impressions || 0).toLocaleString()}</p>
                              <p className="text-xs text-muted-foreground">Impressions</p>
                            </div>
                            <div className="text-center p-3 bg-green-50 rounded-lg">
                              <p className="text-2xl font-bold text-green-700">{(selectedCampaign.clicks || 0).toLocaleString()}</p>
                              <p className="text-xs text-muted-foreground">Clicks</p>
                            </div>
                            <div className="text-center p-3 bg-purple-50 rounded-lg">
                              <p className="text-2xl font-bold text-purple-700">{(selectedCampaign.conversions || 0).toLocaleString()}</p>
                              <p className="text-xs text-muted-foreground">Conversions</p>
                            </div>
                            <div className="text-center p-3 bg-amber-50 rounded-lg">
                              <p className="text-2xl font-bold text-amber-700">{formatCurrency(selectedCampaign.spend || 0)}</p>
                              <p className="text-xs text-muted-foreground">Total Spend</p>
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          ) : (
            <Card className="h-[calc(100vh-320px)] flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <Megaphone className="h-16 w-16 mx-auto mb-4 opacity-20" />
                <h3 className="text-lg font-medium mb-1">Select a Campaign</h3>
                <p className="text-sm">Click on a campaign from the list to view its details</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketingCampaignsPage;
